import { Anchor, Code, Table, Text } from '@mantine/core'
import { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Link } from 'react-router'
import { DataTable } from '@/components/ui-shared/Tables/DataTable'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import { showToast } from '@/theme/notifications'
import { AiBridge } from '@/types/aiBridge'
import { Device } from '@/types/device'
import { CrudAction, Resource } from '@/types/permissions'
import { AiBridgeLastOperation } from '../AiBridgeLastOperation'
import { DeleteAiBridgeModal } from '../DeleteAiBridgeModal'
import { RestartAiBridgeModal } from '../RestartAiBridgeModal'
import { useAiBridgeOperationLog } from '../useAiBridgeOperationLog'
import { useAiBridgeTest } from '../useAiBridgeTest'
import { AiBridgeListActions } from './AiBridgeListActions'

type AiBridgeListProps = {
  aiBridges: AiBridge[]
  devices: Device[]
  isLoading?: boolean
  hasError?: boolean
  onReload?: () => void
}

export const AiBridgeList = ({
  aiBridges,
  devices,
  isLoading,
  hasError,
  onReload
}: AiBridgeListProps) => {
  // The bridge serializer returns device_uuid only, so names come from the device list.
  const deviceNames = new Map(
    devices.map((device) => [device.id, device.label])
  )

  const [canManageAiBridges] = useHasPermissions(Resource.VlmPipelines, [
    CrudAction.Create
  ])

  const [canDeleteAiBridge] = useHasPermissions(Resource.VlmPipelines, [
    CrudAction.Delete
  ])

  const [aiBridgeToDelete, setAiBridgeToDelete] = useState<AiBridge | null>(
    null
  )

  const [aiBridgeToRestart, setAiBridgeToRestart] = useState<AiBridge | null>(
    null
  )
  const [testedBridgeId, setTestedBridgeId] = useState<string | null>(null)

  const { testBridge, isTesting } = useAiBridgeTest()
  const { operationLog, recordOperation } = useAiBridgeOperationLog()

  // No result column any more, so the outcome surfaces as a toast and is kept in the
  // operation log shown on the bridge's details drawer.
  const handleTest = async (bridgeId: string) => {
    setTestedBridgeId(bridgeId)

    const outcome = await testBridge(bridgeId)

    recordOperation(bridgeId, { type: 'test', ...outcome })
    showToast(
      outcome.message,
      outcome.status === 'passed' ? 'success' : 'error'
    )
  }

  return (
    <>
      <DataTable
        hasItems={aiBridges.length > 0}
        isLoading={isLoading}
        hasError={hasError}
        emptyMessage={<FormattedMessage id="aiBridge.list.empty" />}
        onReload={onReload}
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={280}>
              <FormattedMessage id="aiBridge.fields.name" />
            </Table.Th>

            <Table.Th>
              <FormattedMessage id="aiBridge.fields.device" />
            </Table.Th>

            <Table.Th>
              <FormattedMessage id="aiBridge.fields.path" />
            </Table.Th>

            <Table.Th w={280}>
              <FormattedMessage id="aiBridge.fields.lastOperation" />
            </Table.Th>

            <Table.Th>&nbsp;</Table.Th>
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {aiBridges.map((aiBridge) => (
            <Table.Tr key={aiBridge.id}>
              <Table.Td>
                <Anchor
                  component={Link}
                  size="sm"
                  underline="hover"
                  c="blue.8"
                  fw="bold"
                  to={aiBridge.id}
                >
                  {aiBridge.name}
                </Anchor>
              </Table.Td>

              <Table.Td>
                <Text size="xs">
                  {deviceNames.get(aiBridge.device_uuid) ?? (
                    <Code>{aiBridge.device_uuid}</Code>
                  )}
                </Text>
              </Table.Td>

              <Table.Td>
                <Code>{aiBridge.path}</Code>
              </Table.Td>

              <Table.Td>
                <AiBridgeLastOperation operation={operationLog[aiBridge.id]} />
              </Table.Td>

              <Table.Td>
                <AiBridgeListActions
                  canManageAiBridge={canManageAiBridges}
                  canDeleteAiBridge={canDeleteAiBridge}
                  isTesting={isTesting && testedBridgeId === aiBridge.id}
                  onTestAiBridge={() => void handleTest(aiBridge.id)}
                  onRestartAiBridge={() => setAiBridgeToRestart(aiBridge)}
                  onDeleteAiBridge={() => setAiBridgeToDelete(aiBridge)}
                />
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </DataTable>

      <RestartAiBridgeModal
        opened={!!aiBridgeToRestart}
        aiBridge={aiBridgeToRestart || undefined}
        onClose={() => setAiBridgeToRestart(null)}
        onRestart={() => setAiBridgeToRestart(null)}
      />

      <DeleteAiBridgeModal
        opened={!!aiBridgeToDelete}
        aiBridge={aiBridgeToDelete || undefined}
        onClose={() => setAiBridgeToDelete(null)}
        onDelete={() => {
          setAiBridgeToDelete(null)
          void onReload?.()
        }}
      />
    </>
  )
}
